import {
  ArchiveIcon,
  BookOpenIcon,
  CalendarCheckIcon,
  CalendarPlusIcon,
  CarFrontIcon,
  ClipboardListIcon,
  FilesIcon,
  LayoutDashboardIcon,
  LayoutGridIcon,
  MenuIcon,
  MessageSquareIcon,
  NewspaperIcon,
  TicketIcon,
  UsersIcon,
} from 'lucide-react';
import { useState, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router';

import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { PlatformMenuOverlay } from '@/platform/components/platform-menu-overlay';
import {
  resolveMobileContextTabs,
  type ResolvedMobileTab,
} from '@/platform/lib/mobile-context-tabs';

const tabClass =
  'flex h-16 min-h-16 min-w-0 w-full flex-col items-center justify-center gap-0.5 px-1.5 text-xs whitespace-nowrap text-muted-foreground';

/** Four stable slots under `lg`: Menü plus three frequent targets. */
export function PlatformBottomNav() {
  const location = useLocation();
  return <PlatformBottomNavInner key={`${location.pathname}${location.search}`} />;
}

function PlatformBottomNavInner() {
  const me = useMe();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [slot2, slot3, slot4] = resolveMobileContextTabs(location.pathname, me.data);
  const targets = [slot2, slot3, slot4].flatMap((tab) => (tab.kind === 'link' ? [tab.to] : []));

  return (
    <>
      <nav
        aria-label="Hauptnavigation"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        <ul className="grid min-h-16 grid-cols-4">
          <li>
            <button
              type="button"
              className={cn(tabClass, menuOpen && 'font-bold text-primary')}
              aria-label="Menü"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MenuIcon className="size-5" aria-hidden />
              <span className="max-w-full truncate">Menü</span>
            </button>
          </li>
          <li>
            <ContextTab tab={slot2} targets={targets} />
          </li>
          <li>
            <ContextTab tab={slot3} targets={targets} />
          </li>
          <li>
            <ContextTab tab={slot4} targets={targets} />
          </li>
        </ul>
      </nav>
      {me.data ? (
        <PlatformMenuOverlay open={menuOpen} onOpenChange={setMenuOpen} user={me.data} />
      ) : null}
    </>
  );
}

function ContextTab({ tab, targets }: { tab: ResolvedMobileTab; targets: readonly string[] }) {
  if (tab.kind === 'action') {
    return <RegisteredAction Component={tab.Component} />;
  }

  if (tab.kind === 'empty') {
    return <span className={tabClass} aria-hidden />;
  }

  const icon = <TabGlyph tab={tab} />;
  const exact =
    tab.to === '/start' ||
    targets.some((other) => other !== tab.to && other.startsWith(`${tab.to}/`));
  return (
    <NavLink
      to={tab.to}
      end={exact}
      className={({ isActive }) => cn(tabClass, isActive && 'font-bold text-primary')}
      aria-label={tab.label}
    >
      {icon}
      <span className="max-w-full truncate">{tab.label}</span>
    </NavLink>
  );
}

function RegisteredAction({ Component }: { Component: ComponentType }) {
  return <Component />;
}

function TabGlyph({ tab }: { tab: ResolvedMobileTab }) {
  if (tab.id === 'news' || tab.label === 'News') {
    return <NewspaperIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('ticket')) {
    return <TicketIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('chat')) {
    return <MessageSquareIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('wissen') || tab.label === 'Wissen') {
    return <BookOpenIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('book') || tab.label === 'Buchen') {
    return <CalendarCheckIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('vehicle') || tab.label === 'Fahrzeuge') {
    return <CarFrontIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('request') || tab.label === 'Anträge') {
    return <ClipboardListIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('beantrag') || tab.label === 'Beantragen') {
    return <CalendarPlusIcon className="size-5" aria-hidden />;
  }
  if (tab.label === 'Archiv') {
    return <ArchiveIcon className="size-5" aria-hidden />;
  }
  if (tab.label === 'Vorgänge') {
    return <FilesIcon className="size-5" aria-hidden />;
  }
  if (tab.label === 'Vorlagen' || tab.label === 'Planung') {
    return <LayoutGridIcon className="size-5" aria-hidden />;
  }
  if (tab.id.includes('channel') || tab.label === 'Channels') {
    return <UsersIcon className="size-5" aria-hidden />;
  }
  if (tab.label === 'Übersicht' || tab.id.includes('overview') || tab.id.includes('home')) {
    return <LayoutDashboardIcon className="size-5" aria-hidden />;
  }
  return <LayoutGridIcon className="size-5" aria-hidden />;
}
